import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Langs, { Localize } from "../../../app/lang/langs";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import {
    startQuizAttempt,
    submitAnswer,
    finishAttempt,
    fetchCollabPendingQuizzes,
} from "../vsecuriteSlice";
import { showErrorToast } from "../../../app/utils/helpers";
import QuizResults from "./QuizResults";

/* ── Brand / palette constants ── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255,144,21,0.08)";
const ORANGE_GLOW = "rgba(255,144,21,0.25)";
const ORANGE_GRADIENT = "linear-gradient(135deg, #FF8701, #FFC100)";
const BORDER_SUBTLE = "rgba(0,0,0,0.06)";
const TEXT_PRIMARY = "#111318";
const TEXT_SECONDARY = "#4b5263";
const TEXT_MUTED = "#8b92a0";
const SURFACE = "rgba(255,255,255,0.72)";
const BG_GRADIENT = "linear-gradient(160deg, #f7f8fa 0%, #eef0f4 50%, #e8eaef 100%)";
const GREEN = "#16a34a";
const EASE = "cubic-bezier(0.4,0,0.2,1)";

/**
 * QuizTake
 * Renders all quiz questions at once (same UX pattern as CollabMcqView).
 * Numbered navigation chips at the top let the user jump between questions.
 * Submit button at the bottom — enabled only when all questions are answered.
 *
 * @param {object}   props
 * @param {object}   props.quiz       - Quiz meta: { id, title, passing_score }
 * @param {Function} props.onFinish   - Called when the user exits after completing
 * @param {Function} props.onBack     - Called if the user exits before starting
 */
export default function QuizTake({ quiz, onFinish, onBack }) {
    const dispatch = useDispatch();
    const lang = useSelector((state) => state.settings.lang);
    const isMobile = useSelector((state) => state.settings.isMobile);
    const attemptResults = useSelector((state) => state.vsecurite.quizzes.attemptResults);

    const theme = useTheme();
    const isSmallQuery = useMediaQuery(theme.breakpoints.down("sm"));
    const isSmall = isMobile === 2 || isSmallQuery;

    /* ── Phase: 'loading' | 'quiz' | 'results' ── */
    const [phase, setPhase] = useState("loading");

    /* ── Questions array loaded from startQuizAttempt ── */
    const [questions, setQuestions] = useState([]);

    /* ── Current attempt id ── */
    const [attemptId, setAttemptId] = useState(null);

    /* ── Selections: { [questionId]: optionId } for single, { [questionId]: optionId[] } for multiple ── */
    const [selections, setSelections] = useState({});

    /* ── Submitting state while sequentially posting answers ── */
    const [submitting, setSubmitting] = useState(false);

    /* ── Collected review data: [{ question, answerResult }] ── */
    const [answerReview, setAnswerReview] = useState([]);

    /* ── Prevent double-fire in React StrictMode ── */
    const startedRef = useRef(false);

    /* ── Refs for scroll-to-question navigation ── */
    const scrollContainerRef = useRef(null);
    const questionRefs = useRef({});

    /* ── Keyframes injected once ── */
    const keyframes = `
        @keyframes quizFadeInUp {
            from { opacity: 0; transform: translateY(16px); }
            to   { opacity: 1; transform: translateY(0); }
        }
    `;

    /* ── Load questions on mount ── */
    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;

        (async () => {
            const result = await dispatch(startQuizAttempt({ id_quiz: quiz.id }));
            const responseData = result.payload?.responseData;

            if (!responseData?.attempt || !responseData?.questions?.length) {
                showErrorToast(Localize("VSEC_TOAST_START_QUIZ_ERROR", lang));
                if (onBack) onBack();
                return;
            }

            setAttemptId(responseData.attempt.id);
            setQuestions(responseData.questions);
            setPhase("quiz");
        })();
    }, []);

    /* ── Selection helpers ── */
    const handleSelectionChange = useCallback((questionId, optionId, isMulti) => {
        setSelections((prev) => {
            if (isMulti) {
                const current = prev[questionId] || [];
                if (current.includes(optionId)) {
                    /* Keep at least one selected */
                    if (current.length === 1) return prev;
                    return { ...prev, [questionId]: current.filter((id) => id !== optionId) };
                }
                return { ...prev, [questionId]: [...current, optionId] };
            }
            return { ...prev, [questionId]: optionId };
        });
    }, []);

    /* ── Scroll to a question by id ── */
    const scrollToQuestion = useCallback((questionId) => {
        const el = questionRefs.current[questionId];
        const container = scrollContainerRef.current;
        if (el && container) {
            const containerTop = container.getBoundingClientRect().top;
            const elTop = el.getBoundingClientRect().top;
            const offset = elTop - containerTop + container.scrollTop - 16;
            container.scrollTo({ top: offset, behavior: "smooth" });
        }
    }, []);

    /* ── Derived counts ── */
    const answeredCount = questions.filter((q) => {
        const s = selections[q.id];
        return Array.isArray(s) ? s.length > 0 : s !== undefined && s !== null;
    }).length;

    const allAnswered = answeredCount === questions.length && questions.length > 0;
    const progressPercent = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

    /* ── Submit: post each answer in sequence, then finish ── */
    const handleSubmit = async () => {
        if (!allAnswered || submitting) return;
        setSubmitting(true);

        try {
            const collected = [];

            for (const question of questions) {
                const sel = selections[question.id];
                const selectedIds = Array.isArray(sel) ? sel : [sel];

                const res = await dispatch(
                    submitAnswer({
                        id_attempt: attemptId,
                        id_question: question.id,
                        selected_option_ids: selectedIds,
                    })
                );

                if (res.error) {
                    showErrorToast(Localize("VSEC_TOAST_ANSWER_ERROR", lang));
                    setSubmitting(false);
                    return;
                }

                const answerResult = res.payload?.responseData?.answerResult;
                if (answerResult) {
                    collected.push({ question, answerResult });
                }
            }

            const finishRes = await dispatch(finishAttempt({ id_attempt: attemptId }));
            if (finishRes.error) {
                showErrorToast(Localize("VSEC_TOAST_FINALIZATION_ERROR", lang));
                setSubmitting(false);
                return;
            }

            setAnswerReview(collected);
            setPhase("results");
        } catch {
            showErrorToast(Localize("VSEC_TOAST_ANSWER_ERROR", lang));
            setSubmitting(false);
        }
    };

    /* ── Retake: re-mount from scratch ── */
    const handleRetake = useCallback(() => {
        startedRef.current = false;
        setPhase("loading");
        setQuestions([]);
        setSelections({});
        setAttemptId(null);
        setSubmitting(false);
        setAnswerReview([]);
        /* Re-trigger load */
        startedRef.current = false;
        (async () => {
            const result = await dispatch(startQuizAttempt({ id_quiz: quiz.id }));
            const responseData = result.payload?.responseData;
            if (!responseData?.attempt || !responseData?.questions?.length) {
                showErrorToast(Localize("VSEC_TOAST_START_QUIZ_ERROR", lang));
                if (onBack) onBack();
                return;
            }
            setAttemptId(responseData.attempt.id);
            setQuestions(responseData.questions);
            setPhase("quiz");
        })();
    }, [dispatch, quiz.id, lang, onBack]);

    const handleExit = useCallback(() => {
        dispatch(fetchCollabPendingQuizzes());
        onFinish();
    }, [dispatch, onFinish]);

    /* ── Render: loading ── */
    if (phase === "loading") {
        return (
            <Box sx={{
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                minHeight: 280, gap: 2,
            }}>
                <CircularProgress sx={{ color: ORANGE }} size={44} thickness={3.5} />
                <Typography sx={{ fontSize: "0.9rem", color: TEXT_MUTED, fontFamily: "'Inter',sans-serif" }}>
                    <Langs str="VSEC_QUIZ_LOADING" />
                </Typography>
            </Box>
        );
    }

    /* ── Render: results ── */
    if (phase === "results") {
        return (
            <Box sx={{
                maxHeight: isSmall ? "100%" : "calc(100vh - 160px)",
                overflowY: "auto",
                pr: isSmall ? 0 : 0.5,
                "&::-webkit-scrollbar": { width: "5px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": { background: "rgba(255,144,21,0.25)", borderRadius: "3px" },
            }}>
                <QuizResults
                    results={attemptResults}
                    quiz={quiz}
                    onRetake={handleRetake}
                    onExit={handleExit}
                />
            </Box>
        );
    }

    /* ── Render: quiz ── */
    return (
        <>
            <style>{keyframes}</style>
            <Box sx={{
                background: BG_GRADIENT,
                borderRadius: isSmall ? 0 : "20px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: isSmall ? "100%" : "calc(100vh - 160px)",
                mt: 0,
            }}>

                {/* ── Header ── */}
                <Box sx={{
                    background: "rgba(255,255,255,0.85)",
                    borderBottom: `1px solid ${BORDER_SUBTLE}`,
                    px: isSmall ? 1.5 : { xs: 2, sm: 3 },
                    py: isSmall ? 1.2 : 1.8,
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.2 }}>
                        <Stack direction="row" alignItems="center" spacing={1.2} sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{
                                width: 32, height: 32, borderRadius: "9px",
                                background: ORANGE_LIGHT,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0,
                            }}>
                                <QuizOutlinedIcon sx={{ color: ORANGE, fontSize: 18 }} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{
                                    fontWeight: 700, fontSize: isSmall ? "0.88rem" : "0.95rem",
                                    color: TEXT_PRIMARY, letterSpacing: "-0.01em",
                                    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                }}>
                                    {quiz.title}
                                </Typography>
                                <Typography sx={{ fontSize: "0.75rem", color: TEXT_MUTED, fontFamily: "'Inter',sans-serif" }}>
                                    {answeredCount}/{questions.length} {Localize("VSEC_ANSWERED_LABEL", lang)}
                                </Typography>
                            </Box>
                        </Stack>

                        <Tooltip title={Localize("VSEC_TOOLTIP_QUIT_QUIZ", lang)} placement="left">
                            <IconButton
                                size="small"
                                onClick={onBack}
                                sx={{
                                    color: TEXT_MUTED,
                                    border: `1px solid ${BORDER_SUBTLE}`,
                                    borderRadius: "8px", p: "6px",
                                    minWidth: 36, minHeight: 36,
                                    "&:hover": { background: "rgba(0,0,0,0.05)", color: TEXT_SECONDARY },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    {/* Mobile: progress bar instead of chips when many questions */}
                    {isSmall && questions.length > 12 ? (
                        <Box sx={{ mt: 0.5 }}>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercent}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: "rgba(0,0,0,0.06)",
                                    "& .MuiLinearProgress-bar": {
                                        background: ORANGE_GRADIENT,
                                        borderRadius: 3,
                                    },
                                }}
                            />
                        </Box>
                    ) : (
                        /* Question number navigation chips */
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                            {questions.map((q, i) => {
                                const s = selections[q.id];
                                const answered = Array.isArray(s) ? s.length > 0 : s !== undefined && s !== null;
                                return (
                                    <Box
                                        key={q.id}
                                        onClick={() => scrollToQuestion(q.id)}
                                        sx={{
                                            width: isSmall ? 32 : 28,
                                            height: isSmall ? 32 : 28,
                                            borderRadius: "8px",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontSize: isSmall ? "0.75rem" : "0.7rem",
                                            fontWeight: 700,
                                            fontFamily: "'Inter',sans-serif",
                                            cursor: "pointer", userSelect: "none",
                                            background: answered ? ORANGE_GRADIENT : "rgba(0,0,0,0.05)",
                                            color: answered ? "#fff" : TEXT_MUTED,
                                            border: `1px solid ${answered ? "transparent" : "rgba(0,0,0,0.08)"}`,
                                            transition: `all 300ms ${EASE}`,
                                            "&:hover": { transform: "scale(1.15)" },
                                            "&:active": { transform: "scale(0.95)" },
                                        }}
                                    >
                                        {i + 1}
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Box>

                {/* ── Scrollable questions ── */}
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        px: isSmall ? 1.5 : { xs: 2, sm: 3 },
                        pt: 2.5,
                        pb: isSmall ? 10 : 3,
                        WebkitOverflowScrolling: "touch",
                        "&::-webkit-scrollbar": { width: "5px" },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": {
                            background: "rgba(255,144,21,0.20)",
                            borderRadius: "3px",
                            "&:hover": { background: "rgba(255,144,21,0.35)" },
                        },
                    }}
                >
                    {questions.map((question, idx) => {
                        const s = selections[question.id];
                        const isAnswered = Array.isArray(s) ? s.length > 0 : s !== undefined && s !== null;
                        const isMulti = question.type === "multiple";

                        return (
                            <Paper
                                key={question.id}
                                ref={(el) => { questionRefs.current[question.id] = el; }}
                                elevation={0}
                                sx={{
                                    mb: 2.5,
                                    borderRadius: isSmall ? "14px" : "18px",
                                    background: SURFACE,
                                    border: `1px solid ${isAnswered ? "rgba(255,144,21,0.18)" : BORDER_SUBTLE}`,
                                    overflow: "hidden",
                                    position: "relative",
                                    transition: `all 350ms ${EASE}`,
                                    animation: `quizFadeInUp 500ms ${EASE} ${idx * 60}ms both`,
                                    "&:hover": isSmall ? {} : {
                                        border: `1px solid rgba(255,144,21,0.22)`,
                                        boxShadow: `0 4px 24px rgba(255,144,21,0.08)`,
                                    },
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0, left: 0, right: 0, height: "2px",
                                        background: isAnswered
                                            ? ORANGE_GRADIENT
                                            : `linear-gradient(90deg, transparent 10%, ${ORANGE_GLOW} 50%, transparent 90%)`,
                                        transition: `all 400ms ${EASE}`,
                                    },
                                }}
                            >
                                {/* Question header */}
                                <Box sx={{
                                    px: isSmall ? 2 : { xs: 2.5, sm: 3 },
                                    pt: isSmall ? 2 : 2.5,
                                    pb: 1.5,
                                    display: "flex", alignItems: "flex-start", gap: 1.5,
                                }}>
                                    <Chip
                                        label={idx + 1}
                                        size="small"
                                        sx={{
                                            minWidth: 30, height: 28, borderRadius: "9px",
                                            fontWeight: 700, fontSize: "0.78rem", mt: 0.3,
                                            background: isAnswered ? "rgba(255,144,21,0.12)" : "rgba(0,0,0,0.04)",
                                            color: isAnswered ? ORANGE : TEXT_MUTED,
                                            border: `1px solid ${isAnswered ? "rgba(255,144,21,0.2)" : "rgba(0,0,0,0.06)"}`,
                                            transition: `all 350ms ${EASE}`,
                                            "& .MuiChip-label": { px: 1 },
                                        }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{
                                            fontSize: isSmall ? "0.95rem" : { xs: "1rem", sm: "1.05rem" },
                                            fontWeight: 600, letterSpacing: "-0.02em",
                                            color: TEXT_PRIMARY, lineHeight: 1.45,
                                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                        }}>
                                            {question.question_text}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: "0.72rem", color: TEXT_MUTED,
                                            fontWeight: 500, mt: 0.3, fontFamily: "'Inter',sans-serif",
                                        }}>
                                            {isMulti
                                                ? Localize("VSEC_ANSWER_TYPE_MULTI", lang)
                                                : Localize("VSEC_ANSWER_TYPE_SINGLE", lang)}
                                        </Typography>
                                    </Box>
                                    {isAnswered && (
                                        <Fade in timeout={300}>
                                            <CheckCircleOutlineIcon sx={{
                                                color: GREEN, fontSize: 22, mt: 0.4,
                                                filter: "drop-shadow(0 1px 3px rgba(22,163,74,0.2))",
                                            }} />
                                        </Fade>
                                    )}
                                </Box>

                                {/* Divider */}
                                <Box sx={{
                                    height: "1px", mx: isSmall ? 2 : { xs: 2.5, sm: 3 },
                                    background: `linear-gradient(90deg, transparent, ${BORDER_SUBTLE}, transparent)`,
                                }} />

                                {/* Options */}
                                <Box sx={{ px: isSmall ? 1.5 : { xs: 2, sm: 2.5 }, py: 1.5 }}>
                                    {isMulti ? (
                                        <FormGroup>
                                            {(question.options || []).map((option) => {
                                                const isSelected = Array.isArray(selections[question.id])
                                                    ? selections[question.id].includes(option.id)
                                                    : false;
                                                return (
                                                    <FormControlLabel
                                                        key={option.id}
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={isSelected}
                                                                onChange={() => handleSelectionChange(question.id, option.id, true)}
                                                                sx={{
                                                                    color: "rgba(0,0,0,0.2)",
                                                                    "&.Mui-checked": { color: ORANGE },
                                                                    transition: `all 250ms ${EASE}`,
                                                                    p: isSmall ? "8px" : undefined,
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Typography sx={{
                                                                fontSize: isSmall ? "0.9rem" : "0.92rem",
                                                                fontWeight: isSelected ? 550 : 420,
                                                                color: isSelected ? TEXT_PRIMARY : TEXT_SECONDARY,
                                                                transition: `all 250ms ${EASE}`,
                                                                fontFamily: "'Inter',sans-serif",
                                                            }}>
                                                                {option.option_text}
                                                            </Typography>
                                                        }
                                                        sx={{
                                                            mx: 0, mb: 0.3,
                                                            px: isSmall ? 1 : 1.5,
                                                            py: isSmall ? 1 : 0.8,
                                                            borderRadius: "12px",
                                                            minHeight: isSmall ? 48 : "auto",
                                                            border: `1px solid ${isSelected ? "rgba(255,144,21,0.22)" : "transparent"}`,
                                                            background: isSelected ? "rgba(255,144,21,0.04)" : "transparent",
                                                            transition: `all 250ms ${EASE}`,
                                                            "&:hover": {
                                                                background: isSelected ? "rgba(255,144,21,0.07)" : "rgba(0,0,0,0.025)",
                                                            },
                                                            "&:active": isSmall ? {
                                                                transform: "scale(0.98)",
                                                                background: isSelected ? "rgba(255,144,21,0.10)" : "rgba(0,0,0,0.04)",
                                                            } : {},
                                                        }}
                                                    />
                                                );
                                            })}
                                        </FormGroup>
                                    ) : (
                                        <RadioGroup
                                            value={selections[question.id] !== undefined ? selections[question.id] : ""}
                                            onChange={(e) => handleSelectionChange(question.id, Number(e.target.value), false)}
                                        >
                                            {(question.options || []).map((option) => {
                                                const isSelected = selections[question.id] === option.id;
                                                return (
                                                    <FormControlLabel
                                                        key={option.id}
                                                        value={option.id}
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                sx={{
                                                                    color: "rgba(0,0,0,0.2)",
                                                                    "&.Mui-checked": { color: ORANGE },
                                                                    transition: `all 250ms ${EASE}`,
                                                                    p: isSmall ? "8px" : undefined,
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Typography sx={{
                                                                fontSize: isSmall ? "0.9rem" : "0.92rem",
                                                                fontWeight: isSelected ? 550 : 420,
                                                                color: isSelected ? TEXT_PRIMARY : TEXT_SECONDARY,
                                                                transition: `all 250ms ${EASE}`,
                                                                fontFamily: "'Inter',sans-serif",
                                                            }}>
                                                                {option.option_text}
                                                            </Typography>
                                                        }
                                                        sx={{
                                                            mx: 0, mb: 0.3,
                                                            px: isSmall ? 1 : 1.5,
                                                            py: isSmall ? 1 : 0.8,
                                                            borderRadius: "12px",
                                                            minHeight: isSmall ? 48 : "auto",
                                                            border: `1px solid ${isSelected ? "rgba(255,144,21,0.22)" : "transparent"}`,
                                                            background: isSelected ? "rgba(255,144,21,0.04)" : "transparent",
                                                            transition: `all 250ms ${EASE}`,
                                                            "&:hover": {
                                                                background: isSelected ? "rgba(255,144,21,0.07)" : "rgba(0,0,0,0.025)",
                                                            },
                                                            "&:active": isSmall ? {
                                                                transform: "scale(0.98)",
                                                                background: isSelected ? "rgba(255,144,21,0.10)" : "rgba(0,0,0,0.04)",
                                                            } : {},
                                                        }}
                                                    />
                                                );
                                            })}
                                        </RadioGroup>
                                    )}
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>

                {/* ── Sticky bottom submit bar ── */}
                <Box sx={{
                    position: "sticky",
                    bottom: 0,
                    background: "rgba(255,255,255,0.90)",
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    borderTop: `1px solid ${BORDER_SUBTLE}`,
                    px: isSmall ? 1.5 : { xs: 2, sm: 3 },
                    py: isSmall ? 1.5 : 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    flexDirection: isSmall ? "column" : "row",
                    /* Safe area for devices with home indicator */
                    pb: isSmall ? "calc(12px + env(safe-area-inset-bottom, 0px))" : 1.8,
                }}>
                    <Chip
                        label={`${answeredCount} / ${questions.length}`}
                        size="small"
                        sx={{
                            fontWeight: 700, fontSize: "0.8rem",
                            fontFamily: "'Inter',sans-serif",
                            background: allAnswered ? "rgba(22,163,74,0.08)" : "rgba(0,0,0,0.04)",
                            color: allAnswered ? GREEN : TEXT_MUTED,
                            border: `1px solid ${allAnswered ? "rgba(22,163,74,0.18)" : "rgba(0,0,0,0.06)"}`,
                            borderRadius: "10px",
                            transition: `all 350ms ${EASE}`,
                            "& .MuiChip-label": { px: 1.5 },
                        }}
                    />
                    <Button
                        variant="contained"
                        disabled={!allAnswered || submitting}
                        onClick={handleSubmit}
                        fullWidth={isSmall}
                        endIcon={
                            submitting
                                ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                                : <SendRoundedIcon sx={{ fontSize: "18px !important" }} />
                        }
                        sx={{
                            borderRadius: "14px",
                            px: isSmall ? 3 : 4,
                            py: 1.2,
                            minHeight: 48,
                            textTransform: "none",
                            fontWeight: 650,
                            fontSize: isSmall ? "0.95rem" : "0.92rem",
                            letterSpacing: "-0.01em",
                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                            background: ORANGE_GRADIENT,
                            color: "#fff",
                            boxShadow: `0 4px 16px rgba(255,144,21,0.25), 0 1px 4px rgba(255,144,21,0.15)`,
                            transition: `all 300ms ${EASE}`,
                            "&:hover": {
                                background: ORANGE_GRADIENT,
                                boxShadow: `0 6px 24px rgba(255,144,21,0.35)`,
                                transform: "translateY(-1px)",
                            },
                            "&:active": { transform: "scale(0.98)" },
                            "&.Mui-disabled": {
                                background: "rgba(0,0,0,0.06)",
                                color: "rgba(0,0,0,0.26)",
                                boxShadow: "none",
                            },
                        }}
                    >
                        {submitting
                            ? Localize("VSEC_BTN_FINALIZING", lang)
                            : Localize("VSEC_BTN_SUBMIT_QUIZ", lang)}
                    </Button>
                </Box>
            </Box>
        </>
    );
}
